/**
 * AnnouncementCard — full-screen announcement card shared by
 * announcements_only mode.
 *
 * Pure function; returns a new root each call so AnnouncementsOnlyRenderer
 * can mount/crossfade freely. Optional `image_url` turns on the 2:1 split;
 * onerror handler gracefully collapses the image column if the asset
 * fails so the card never shows a broken-image icon on a big display.
 *
 * Published announcement payload shape — see SignageAnnouncement schema.
 */

export interface AnnouncementCardOptions {
  readonly title: string;
  readonly body?: string;
  readonly imageUrl?: string;
  readonly ctaLabel?: string;
  readonly ctaUrl?: string;
  readonly priority?: string;
  /** Pre-formatted "Posted {time}" string. Omit to hide. */
  readonly publishedAtDisplay?: string;
}

export function renderAnnouncementCard(opts: AnnouncementCardOptions): HTMLElement {
  const card = document.createElement('div');
  card.className = 'ag-ann-card';
  const hasImage = !!(opts.imageUrl && opts.imageUrl.trim());
  if (hasImage) card.classList.add('ag-ann-card--with-image');

  const body = document.createElement('div');
  body.className = 'ag-ann-card__body';

  if (opts.priority === 'urgent') {
    const pill = document.createElement('div');
    pill.className = 'ag-ann-card__priority';
    pill.textContent = 'URGENT';
    body.appendChild(pill);
  }

  const title = document.createElement('h1');
  title.className = 'ag-ann-card__title';
  title.textContent = opts.title;
  body.appendChild(title);

  if (opts.body) {
    const p = document.createElement('p');
    p.className = 'ag-ann-card__text';
    p.textContent = opts.body;
    body.appendChild(p);
  }

  if (opts.ctaLabel) {
    const cta = document.createElement('div');
    cta.className = 'ag-ann-card__cta';
    cta.textContent = opts.ctaUrl
      ? `${opts.ctaLabel} — ${opts.ctaUrl}`
      : opts.ctaLabel;
    body.appendChild(cta);
  }

  if (opts.publishedAtDisplay) {
    const pub = document.createElement('div');
    pub.className = 'ag-ann-card__meta';
    pub.textContent = `Posted ${opts.publishedAtDisplay}`;
    body.appendChild(pub);
  }

  card.appendChild(body);

  if (hasImage) {
    const imgWrap = document.createElement('div');
    imgWrap.className = 'ag-ann-card__image';
    const img = document.createElement('img');
    img.src = opts.imageUrl!;
    img.alt = '';
    img.onerror = () => {
      imgWrap.remove();
      card.classList.remove('ag-ann-card--with-image');
    };
    imgWrap.appendChild(img);
    card.appendChild(imgWrap);
  }

  return card;
}
